import { useCallback, useEffect, useRef } from "react";
import { Platform, type View } from "react-native";

const FOCUSABLE_SELECTOR = 'a[href], button, textarea, input, select, [tabindex], [contenteditable="true"]';

interface UseFocusTrapOptions {
  /** Whether the focus trap is currently active (typically tied to modal visibility). */
  visible: boolean;
  /** Callback invoked when the user presses Escape while the trap is active. */
  onEscape: () => void;
}

interface UseFocusTrapReturn {
  /** Ref to attach to the container element that should trap focus. */
  trapRef: React.RefObject<View | null>;
}

type UseFocusTrapArgs =
  | [options: UseFocusTrapOptions]
  | [trapRef: React.RefObject<View | null>, options: UseFocusTrapOptions];

/**
 * Manages keyboard focus trapping for modal dialogs on web.
 *
 * When `visible` is true the hook:
 * - saves the currently focused element so it can be restored later,
 * - constrains Tab / Shift+Tab to cycle within `trapRef`,
 * - moves focus to the first focusable child after the next paint,
 * - closes on Escape via the provided `onEscape` callback,
 * - restores focus to the previously-focused element when `visible`
 *   becomes false.
 *
 * On native platforms the hook is a no-op.
 */
export function useFocusTrap(...args: UseFocusTrapArgs): UseFocusTrapReturn {
  const internalTrapRef = useRef<View | null>(null);
  const [providedTrapRef, options] = args.length === 2 ? [args[0], args[1]] : [undefined, args[0]];
  const trapRef = providedTrapRef ?? internalTrapRef;
  const { visible, onEscape } = options;
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const onEscapeRef = useRef(onEscape);
  const fallbackTabIndexRef = useRef<{ container: HTMLElement | null; previousValue: string | null } | null>(null);

  const restoreContainerTabIndex = useCallback(() => {
    const fallbackTabIndex = fallbackTabIndexRef.current;

    if (!fallbackTabIndex) {
      return;
    }

    const { container, previousValue } = fallbackTabIndex;

    if (container?.isConnected) {
      if (previousValue === null) {
        container.removeAttribute("tabindex");
      } else {
        container.setAttribute("tabindex", previousValue);
      }
    }

    fallbackTabIndexRef.current = null;
  }, []);

  const ensureContainerFocusable = useCallback((container: HTMLElement) => {
    if (container.hasAttribute("tabindex")) {
      return;
    }

    fallbackTabIndexRef.current = {
      container,
      previousValue: null,
    };
    container.setAttribute("tabindex", "-1");
  }, []);

  const getFocusableElements = useCallback((container: HTMLElement) => {
    return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((element) => {
      if (!element.isConnected) {
        return false;
      }

      if (element.matches(":disabled") || element.getAttribute("aria-disabled") === "true") {
        return false;
      }

      if (element.getAttribute("tabindex") === "-1") {
        return false;
      }

      if (element.hidden || element.getAttribute("aria-hidden") === "true") {
        return false;
      }

      if (element.getClientRects().length === 0) {
        return false;
      }

      const computedStyle = window.getComputedStyle(element);
      if (computedStyle.display === "none" || computedStyle.visibility === "hidden") {
        return false;
      }

      return element.tabIndex >= 0;
    });
  }, []);

  const restorePreviousFocus = useCallback(() => {
    const previousFocus = previousFocusRef.current;

    if (previousFocus?.isConnected && typeof previousFocus.focus === "function") {
      previousFocus.focus();
    }

    previousFocusRef.current = null;
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onEscapeRef.current();
        return;
      }

      if (e.key !== "Tab") return;

      const container = trapRef.current as unknown as HTMLElement | null;
      if (!container) return;

      const focusable = getFocusableElements(container);
      const activeElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;

      if (focusable.length === 0) {
        e.preventDefault();
        ensureContainerFocusable(container);
        container.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (!activeElement || !container.contains(activeElement)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }

      if (activeElement === container) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }

      if (e.shiftKey) {
        if (activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [ensureContainerFocusable, getFocusableElements]
  );

  useEffect(() => {
    onEscapeRef.current = onEscape;
  }, [onEscape]);

  useEffect(() => {
    if (Platform.OS !== "web") return;

    if (!visible) {
      restoreContainerTabIndex();
      restorePreviousFocus();
      return;
    }

    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.addEventListener("keydown", handleKeyDown);

    // Move focus into the container after the next paint.
    const raf = requestAnimationFrame(() => {
      const container = trapRef.current as unknown as HTMLElement | null;
      if (!container) return;

      const firstFocusable = getFocusableElements(container)[0];
      if (firstFocusable) {
        firstFocusable.focus({ preventScroll: true });
        return;
      }

      ensureContainerFocusable(container);
      container.focus({ preventScroll: true });
    });

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("keydown", handleKeyDown);
      restoreContainerTabIndex();
      restorePreviousFocus();
    };
  }, [
    visible,
    ensureContainerFocusable,
    getFocusableElements,
    handleKeyDown,
    restoreContainerTabIndex,
    restorePreviousFocus,
  ]);

  return { trapRef };
}
